import { ColDef, ModuleRegistry } from 'ag-grid-community';
import { ColumnMenuModule, ColumnsToolPanelModule } from 'ag-grid-enterprise';
import { useCallback } from 'react';
import { Badge, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { MdBusiness } from '@react-icons/all-files/md/MdBusiness';
import { MdExtension } from '@react-icons/all-files/md/MdExtension';
import { MdPerson } from '@react-icons/all-files/md/MdPerson';
import { AgGridBase, useAgGridColumnStatePersistence } from '../AgGridBase';
import { Typography } from 'Components/atoms';
import StatTile from 'Components/atoms/StatTile/StatTile';
//import ModeTab from 'Components/atoms/ModeTab/ModeTab';
import { useAgentManagementPreview } from 'Hooks/useAgentManagementPreview';
import { useAgentManagementAgents } from 'Hooks/useAgentManagementAgents';
import { AgentRecord } from 'Core-utils/staticData/staticAgents';
import './styles.scss';

// Register modules once — idempotent, safe to call at module load time.
ModuleRegistry.registerModules([ColumnsToolPanelModule, ColumnMenuModule]);

// ---------------------------------------------------------------------------
// Stat row
// ---------------------------------------------------------------------------

const AgentStatRow = ({ agents }: { agents: AgentRecord[] }) => {
    const total = agents.length;
    const unvetted = agents.filter((a) => !a.certifiedByOwner).length;
    const vendors = new Set(agents.map((a) => a.vendor)).size;
    const critical = agents.filter((a) => a.criticality === 'CRITICAL').length;

    return (
        <div className="inventory-stat-row">
            <StatTile label="Total Agents" value={total} />
            <StatTile label="Unvetted Agents" value={unvetted} />
            <StatTile label="Agent Vendors" value={vendors} />
            <StatTile label="Critical Agents" value={critical} />
        </div>
    );
};

// ---------------------------------------------------------------------------
// Mode tabs
// ---------------------------------------------------------------------------

type AgentGroupBy = 'vendor' | 'harness' | 'owner';

const AGENT_GROUP_TABS: { key: AgentGroupBy; icon: React.ReactNode; title: string; subtitle: string }[] = [
    { key: 'vendor', icon: <MdBusiness />, title: 'Vendor', subtitle: 'By AI provider' },
    { key: 'harness', icon: <MdExtension />, title: 'Harness', subtitle: 'By agent platform' },
    { key: 'owner', icon: <MdPerson />, title: 'Owner', subtitle: 'By team member' },
];

// ---------------------------------------------------------------------------
// Kind badge renderer — shows ROUTINE or SESSION badge
// ---------------------------------------------------------------------------

const KindCellRenderer = (params: any) => {
    const data = params.data as AgentRecord | undefined;
    const isRoutine = data?.routineId !== undefined;
    const kind = isRoutine ? 'ROUTINE' : 'SESSION';
    const bgClass = isRoutine ? 'bg-info' : 'bg-secondary';

    return (
        <div className="d-flex align-items-center h-100">
            <Badge className={`text-uppercase ${bgClass}`}>{kind}</Badge>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Column state persistence
// ---------------------------------------------------------------------------

const AGENT_INVENTORY_COL_STATE_KEY = 'agent-inventory-column-state';

/**
 * Columns visible on first load (or after a column reset).
 * All column IDs not listed here — or listed with hide:true — are hidden by default
 * but remain toggleable via the columns panel.
 */
const DEFAULT_AGENT_COLUMN_STATE = [
    { colId: 'displayName', hide: false },
    { colId: 'kind', hide: false },
    { colId: 'agentId', hide: false },
    { colId: 'ownerName', hide: false },
    { colId: 'harness', hide: false },
    { colId: 'autonomyScore', hide: false },
    { colId: 'mcpToolNames', hide: false },
    { colId: 'sessionCount', hide: false },
    { colId: 'discoveredAt', hide: false },
    { colId: 'certifiedByOwner', hide: false },
    // Off by default — available in the columns panel
    { colId: 'routineId', hide: true },
    { colId: 'vendor', hide: true },
    { colId: 'model', hide: true },
    { colId: 'runFrequency', hide: true },
    { colId: 'lastRanAt', hide: true },
    { colId: 'toolResultTurns', hide: true },
];

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const COLUMN_DEFS: ColDef<AgentRecord>[] = [
    { field: 'displayName', headerName: 'Agent Name', flex: 2, minWidth: 200 },
    {
        colId: 'kind',
        headerName: 'Kind',
        flex: 1,
        minWidth: 120,
        cellRenderer: KindCellRenderer,
    },
    { field: 'agentId', headerName: 'Agent ID', flex: 2, minWidth: 180 },
    {
        field: 'routineId',
        headerName: 'Routine ID',
        flex: 1,
        minWidth: 160,
        valueFormatter: (params) => params.value ?? '-',
    },
    {
        field: 'sessionCount',
        headerName: 'Sessions',
        flex: 1,
        minWidth: 110,
        valueFormatter: (params) => String(params.value ?? 1),
    },
    { field: 'ownerName', headerName: 'Owner', flex: 1, minWidth: 140 },
    { field: 'vendor', headerName: 'Vendor', flex: 1, minWidth: 110 },
    { field: 'harness', headerName: 'Harness', flex: 1, minWidth: 130 },
    { field: 'model', headerName: 'Model', flex: 1, minWidth: 130 },
    {
        field: 'autonomyScore',
        headerName: 'Autonomy Score',
        flex: 1,
        minWidth: 140,
        valueFormatter: (params) =>
            params.value !== null && params.value !== undefined ? `${(params.value as number).toFixed(1)}` : '-',
    },
    {
        field: 'mcpToolNames',
        headerName: 'MCP Tools',
        flex: 2,
        minWidth: 200,
        valueFormatter: (params) => {
            const tools = params.value as string[] | undefined;
            return tools?.length ? tools.join(', ') : '-';
        },
    },
    {
        field: 'toolResultTurns',
        headerName: '# Tool Results',
        flex: 1,
        minWidth: 130,
        valueFormatter: (params) => (params.value !== null ? String(params.value) : '-'),
    },
    { field: 'discoveredAt', headerName: 'Discovered', flex: 1, minWidth: 120 },
    { field: 'runFrequency', headerName: 'Run Frequency', flex: 1, minWidth: 130 },
    {
        field: 'lastRanAt',
        headerName: 'Last Ran',
        flex: 1,
        minWidth: 160,
        valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : '-'),
    },
    {
        field: 'certifiedByOwner',
        headerName: 'Certified',
        flex: 1,
        minWidth: 100,
        valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
    },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AgentInventory = () => {
    const navigate = useNavigate();
    const { isLoading, isEnabled } = useAgentManagementPreview();
    const { agents, loading: agentsLoading } = useAgentManagementAgents();
    //const [activeGroup, setActiveGroup] = useState<AgentGroupBy>('vendor');

    const { applyOnGridReady, onColumnEvent } = useAgGridColumnStatePersistence({
        storageKey: AGENT_INVENTORY_COL_STATE_KEY,
        defaultState: DEFAULT_AGENT_COLUMN_STATE,
    });

    const handleGridReady = useCallback((params: any) => applyOnGridReady(params.api), [applyOnGridReady]);

    const handleColumnEvent = useCallback((params: any) => onColumnEvent(params), [onColumnEvent]);

    /** Reset columns to the default visible set and clear persisted state. */
    const handleResetColumns = useCallback((api: any) => {
        api.applyColumnState({ state: DEFAULT_AGENT_COLUMN_STATE, applyOrder: true });
        localStorage.removeItem(AGENT_INVENTORY_COL_STATE_KEY);
    }, []);

    const getMainMenuItems = useCallback(
        (params: any) => {
            const defaults: string[] = params.defaultItems ?? [];
            return defaults.map((item: any) =>
                item === 'resetColumns'
                    ? { name: 'Reset Columns', action: () => handleResetColumns(params.api) }
                    : item,
            );
        },
        [handleResetColumns],
    );

    if (isLoading || agentsLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center agent-inventory__loading">
                <Spinner animation="border" />
            </div>
        );
    }

    if (!isEnabled) {
        return (
            <div className="bg-text-black p-4 card agent-inventory__locked">
                <Typography variant="subtitle2" color="text-high-emphasis">
                    Agent Management
                </Typography>
                <Typography variant="body3" color="text-medium-emphasis" className="mt-2">
                    Agent Management is a preview feature. Contact your administrator to enable it for this tenant.
                </Typography>
            </div>
        );
    }

    return (
        <div className="agent-inventory pt-4 pb-4 w-100 h-100 d-flex flex-column gap-3">
            <h2 className="m-0 text-text-high-emphasis">Agents</h2>

            <AgentStatRow agents={agents} />

            <div className="agent-inventory__toolbar">
                {AGENT_GROUP_TABS.map((tab) => (
                    <div className={`test-${tab.key}`}></div>
                ))}
            </div>

            <AgGridBase.Root>
                <AgGridBase.Header title="Discovered Agents" rowCount={agents.length} />
                <AgGridBase.Table<AgentRecord>
                    dataMode="client"
                    rowData={agents}
                    columnDefs={COLUMN_DEFS}
                    showSidebar
                    agGridProps={{
                        onGridReady: handleGridReady,
                        onColumnResized: handleColumnEvent,
                        onColumnMoved: handleColumnEvent,
                        onColumnVisible: handleColumnEvent,
                        onColumnPinned: handleColumnEvent,
                        getMainMenuItems,
                        onRowClicked: (event) => {
                            if (event.data?.id) {
                                navigate(`/agents/${event.data.id}`);
                            }
                        },
                        rowClass: 'agent-inventory__row--clickable',
                    }}
                />
            </AgGridBase.Root>
        </div>
    );
};

export default AgentInventory;
